import {
  UseMutationOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query"
import type { ProductType } from "@tfg/db"
import { AxiosError } from "axios"

import { AdminRequests } from "@tfg/types/endpoints/admin"
import { EcobalyseProductType } from "@tfg/types/endpoints/analysis"
import { ProductTypeUpdateValidationType } from "@tfg/types/schemas/admin"

import { httpClient } from "~/backend/axios"
import { queryClient } from "~/backend/queryClient"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

export const useCreateProductTypeMutation = (
  options?: UseMutationOptions<
    ProductType,
    AxiosError,
    AdminRequests.NewProductType
  >
) =>
  useMutation<ProductType, AxiosError, ProductTypeUpdateValidationType>({
    ...options,
    mutationFn: async (productType: ProductTypeUpdateValidationType) => {
      const res = await httpClient.post<ProductType>(
        "/admin/product-types",
        productType
      )
      return res.data
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      await queryClient.invalidateQueries({ queryKey: ["productTypes"] })
    },
    onError: (error, variables) => {
      showAxiosError({
        notificationTitle: `Error ${variables.name}`,
        notificationMessage: error.message || "An error occurred",
        showNotification: true,
        error,
      })
    },
  })

export const useUpdateProductTypeMutation = (
  options?: UseMutationOptions<
    ProductType,
    AxiosError,
    { productId: number; productType: AdminRequests.NewProductType }
  >
) =>
  useMutation<
    ProductType,
    AxiosError,
    { productId: number; productType: AdminRequests.NewProductType }
  >({
    mutationFn: async ({ productId, productType }) => {
      const res = await httpClient.patch<ProductType>(
        `/admin/product-types/${productId}`,
        productType
      )
      return res.data
    },
    onSuccess: async (_data, variables, onMutateResult, context) => {
      options?.onSuccess?.(_data, variables, onMutateResult, context)
      await queryClient.invalidateQueries({ queryKey: ["productTypes"] })
      showSuccessNotification({
        title: "Updated",
        message: `Product type ${variables.productType.name} has been updated`,
      })
    },
    onError: (error, variables) => {
      showAxiosError({
        notificationTitle: `Error ${variables.productType.name}`,
        notificationMessage: error.message || "An error occurred",
        error,
      })
    },
  })

export const useDeleteProductTypeMutation = (
  options?: UseMutationOptions<void, unknown, number>
) => {
  return useMutation<void, unknown, number>({
    ...options,
    mutationFn: async (productId) => {
      return httpClient.delete(`/admin/product-types/${productId}`)
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      showSuccessNotification({
        title: "Deleted",
        message: `Product type ${variables} has been deleted`,
      })
      await queryClient.invalidateQueries({ queryKey: ["productTypes"] })
    },
  })
}

export const useListEcobalyseProductTypesQuery = () =>
  useQuery({
    queryKey: ["ecobalyse", "productTypes"],

    queryFn: async () => {
      const res = await httpClient.get<EcobalyseProductType[]>(
        "/analysis/productTypes"
      )
      return res.data
    },
  })
